/*
 * -----------------------------------------------------------------------
 *  <copyright file="switch-menu/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { ISwitchMenu } from '@interfaces/svt.interface';
import cn from 'clsx';
import React, { useRef } from 'react';
import HeadingMenu from '@components/core/grid-header/heading-menu';
import { useBoolean, useOnClickOutside, useTranslation } from '@hooks/index';

import './styles.scss';
import { textToId } from '@utils/automation-test-helpers';

const SwitchMenu = ({ switchMenu, disable }: { switchMenu: ISwitchMenu[]; disable: boolean }) => {
    const { t } = useTranslation();
    const showMenu = useBoolean(false);

    const switchMenuRef = useRef<HTMLDivElement | null>(null);

    useOnClickOutside(switchMenuRef, () => showMenu.setFalse());

    const onToggleMenu = () => {
        if (showMenu.value) {
            showMenu.setFalse();
        } else {
            showMenu.setTrue();
        }
    };

    const LABEL_KEY = 'common:switchData';

    return (
        <HeadingMenu
            icon={['fal', 'arrow-right-arrow-left']}
            title={t(LABEL_KEY)}
            toggle={onToggleMenu}
            isOpened={showMenu.value}
            disabled={!switchMenu.length || disable || showMenu.value}
            data-testid={textToId(LABEL_KEY)}
        >
            <div ref={switchMenuRef} className="switch-menu">
                {switchMenu.map((menuItem: ISwitchMenu) => (
                    <button
                        key={menuItem.label}
                        type="button"
                        className={cn('switch-menu-item', { active: menuItem.currentValue === menuItem.value })}
                        onClick={() => {
                            menuItem.onClick(menuItem.value);
                            showMenu.setFalse();
                        }}
                    >
                        {menuItem.label}
                    </button>
                ))}
            </div>
        </HeadingMenu>
    );
};

export default SwitchMenu;
